import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { XIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { usePortalContainer } from "@/common/hooks/use-portal-container";
import { Z_LAYERS } from "@/components/ui/z-layers";
import { DialogPortal } from "./DialogPortal";
import { DialogOverlay } from "./DialogOverlay";
import { DialogContentProps } from "./types";

/**
 * Dialog Content Component
 * The main dialog content area with portal and overlay
 */
export const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(({ className, children, container: containerProp, onOpenAutoFocus: onOpenAutoFocusProp, showCloseButton = true, style: styleProp, ...props }, ref) => {
  // Automatically detect shadow root container if not explicitly provided
  // Memoize to prevent hook re-execution on every render
  const autoContainer = usePortalContainer();
  const container = React.useMemo(
    () => containerProp || autoContainer,
    [containerProp, autoContainer]
  );

  // Track when content is fully rendered and visible
  const contentRef = React.useRef<HTMLDivElement | null>(null);
  
  // Handler to prevent dismissal when clicking inside the dialog content
  // This is critical when the dialog is opened from calendar view due to event bubbling
  const handlePointerDownOutside = React.useCallback((event: Event) => {
    const target = event.target as HTMLElement;
    const contentElement = contentRef.current;
    
    // If click is inside the dialog content, prevent dismissal
    if (contentElement && target && contentElement.contains(target)) {
      event.preventDefault();
      return;
    }
    
    // Also check if the click is on a dialog-related element (overlay, portal, etc.)
    // This handles cases where shadow DOM boundaries might cause detection issues
    const isDialogElement = target?.closest('[role="dialog"]') !== null ||
                           target?.closest('[data-radix-dialog-content]') !== null ||
                           target?.closest('[data-radix-dialog-overlay]') !== null ||
                           target?.closest('[data-radix-popover-content]') !== null || 
                           target?.closest('[data-radix-dropdown-menu-content]') !== null ||
                           target?.closest('[data-radix-select-content]') !== null ||
                           target?.closest('[data-radix-select-viewport]') !== null;
    
    if (isDialogElement) {
      event.preventDefault();
      return;
    }
    
    // Allow custom handler if provided
    if (props.onPointerDownOutside) {
      props.onPointerDownOutside(event as any);
    }
  }, [props.onPointerDownOutside]);
  
  // Handler to prevent dismissal on interact outside (similar to pointer down)
  const handleInteractOutside = React.useCallback((event: Event) => {
    const target = event.target as HTMLElement;
    const contentElement = contentRef.current;
    
    // If interaction is inside the dialog content, prevent dismissal
    if (contentElement && target && contentElement.contains(target)) {
      event.preventDefault();
      return;
    }
    
    // Also check if the interaction is on a dialog-related element
    const isDialogElement = target?.closest('[role="dialog"]') !== null ||
                           target?.closest('[data-radix-dialog-content]') !== null ||
                           target?.closest('[data-radix-dialog-overlay]') !== null ||
                           target?.closest('[data-radix-popover-content]') !== null ||
                           target?.closest('[data-radix-dropdown-menu-content]') !== null ||
                           target?.closest('[data-radix-select-content]') !== null ||
                           target?.closest('[data-radix-select-viewport]') !== null;
    
    if (isDialogElement) {
      event.preventDefault();
      return;
    }
    
    // Allow custom handler if provided
    if (props.onInteractOutside) {
      props.onInteractOutside(event as any);
    }
  }, [props.onInteractOutside]);
  
  // Handler to stop event propagation on dialog content clicks
  // This prevents events from bubbling to calendar listeners
  const handleContentClick = React.useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    // Stop propagation to prevent calendar event listeners from interfering
    e.stopPropagation();
  }, []);
  
  const handleContentMouseDown = React.useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    // Stop mousedown propagation - critical for preventing calendar slot selection
    e.stopPropagation();
  }, []);
  
  return (
    <DialogPortal container={container}>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={(node) => {
          // Store ref for outside click detection
          contentRef.current = node;
          
          // Forward ref if provided
          if (typeof ref === 'function') {
            ref(node);
          } else if (ref) {
            (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
          }
        }}
        onOpenAutoFocus={(event) => {
          // Prevent Radix UI from auto-focusing the first focusable element
          // This prevents dropdowns from opening automatically when dialog opens
          event.preventDefault();
          // Allow custom onOpenAutoFocus handler if provided in props
          if (onOpenAutoFocusProp) {
            onOpenAutoFocusProp(event);
          }
        }}
        onPointerDownOutside={handlePointerDownOutside}
        onInteractOutside={handleInteractOutside}
        onClick={handleContentClick}
        onMouseDown={handleContentMouseDown}
        className={cn(
          "fixed left-[50%] top-[50%] flex flex-col w-full max-w-[calc(100vw-1rem)] max-h-[calc(100vh-1rem)] translate-x-[-50%] translate-y-[-50%] border shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 sm:rounded-lg md:w-full z-10000 p-4 sm:p-6 overflow-y-auto overflow-x-hidden",
          className
        )}
        style={(() => {
          return {
            zIndex: Z_LAYERS.dialog,
            backgroundColor: 'var(--color-surface, #ffffff)',
            borderColor: 'var(--color-border, #e5e7eb)',
            color: 'var(--color-text, #111827)',
            // Filter out pointer-events: none from styleProp if it exists
            ...(styleProp && typeof styleProp === 'object' && !Array.isArray(styleProp)
              ? Object.fromEntries(
                  Object.entries(styleProp).filter(([key]) =>
                    key !== 'pointerEvents' && key !== 'pointer-events'
                  )
                )
              : styleProp),
            pointerEvents: 'auto', // CRITICAL: Always ensure dialog content is interactive (must be last to override any other value)
          };
        })()}
        {...props}
      >
        {children}

        {showCloseButton && (
          <DialogPrimitive.Close
            className="absolute top-4 sm:top-8 rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:pointer-events-none cursor-pointer end-4 sm:end-8"
            style={{
              color: 'var(--color-text-muted, #6b7280)',
              backgroundColor: 'transparent',
              borderColor: 'var(--color-border, #e5e7eb)'
            }}
          >
            <XIcon className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
});

DialogContent.displayName = DialogPrimitive.Content.displayName;
